<template>
  <teleport to="body">
    <div
      v-if="localVisible && steps && steps.length"
      ref="tourRef"
      tabindex="1"
      :class="tourWrapperClass"
      :style="tourStyle"
    >
      <!--tour content-->
      <div :class="ns.e('content')">
        <span
          ref="popoverReferenceRef"
          :class="[ns.e('trigger')]"
          :style="currentTriggerStyle"
        />
        <span
          v-if="!currentMask"
          :class="[ns.e('trigger-shadow')]"
          :style="currentTriggerStyle"
        />
        <el-popover
          ref="popover"
          :visible="localVisible"
          :teleported="false"
          virtual-triggering
          :virtual-ref="popoverReferenceRef"
          v-bind="currentPopProps"
        >
          <template #default>
            <el-button
              v-if="showClose"
              :class="ns.e('close')"
              type="info"
              text
              @click="handleClose"
              ><el-icon><close /></el-icon
            ></el-button>
            <div :class="[ns.e('header')]">
              <template v-if="currentStep && currentStep.cover">
                <tour-content-render
                  :class="ns.e('cover')"
                  :content="currentStep.cover || ''"
                />
              </template>
              <tour-content-render
                :class="ns.e('title')"
                :content="currentStep?.title ?? ''"
              />
            </div>
            <div :class="ns.e('body')">
              <tour-content-render
                :class="ns.e('description')"
                :content="currentStep?.description ?? ''"
              />
            </div>
            <div :class="ns.e('footer')">
              <div :class="ns.e('indicators')">
                <slot name="indicators" :current="localCurrent" :total="total">
                  <template v-if="indicatorsRender">
                    <tour-content-render
                      :content="
                        () =>
                          indicatorsRender
                            ? indicatorsRender(localCurrent, total)
                            : ''
                      "
                    />
                  </template>
                  <template v-else>
                    <template v-if="!dot">
                      <span :class="ns.e('indicator-text')"
                        >{{ localCurrent }}/{{ total }}</span
                      >
                    </template>
                    <template v-else>
                      <template v-for="count in total" :key="count">
                        <span
                          :class="{
                            [ns.e('indicator-item')]: true,
                            [ns.is('active')]: count === localCurrent,
                          }"
                        />
                      </template>
                    </template>
                  </template>
                </slot>
              </div>
              <div :class="ns.e('actions')">
                <el-button
                  v-if="showSkip"
                  v-show="!showFinish"
                  :type="type === 'primary' ? 'primary' : ''"
                  size="small"
                  @click="handleSkip"
                  >{{ t('el.tour.skipButtonText') }}</el-button
                >
                <el-button
                  v-show="showPrev"
                  :type="type === 'primary' ? 'primary' : ''"
                  size="small"
                  @click="handlePrev"
                  >{{ t('el.tour.prevButtonText') }}</el-button
                >
                <el-button
                  v-show="showNext"
                  :type="type === 'primary' ? '' : 'primary'"
                  size="small"
                  @click="handleNext"
                  >{{ t('el.tour.nextButtonText') }}</el-button
                >
                <el-button
                  v-show="showFinish"
                  :type="type === 'primary' ? '' : 'primary'"
                  size="small"
                  @click="handleFinish"
                  >{{ t('el.tour.finishButtonText') }}</el-button
                >
              </div>
            </div>
          </template>
        </el-popover>
      </div>
      <el-tour-v2-mask
        ref="maskRef"
        v-bind="currentTrigger"
        :mask="currentMask"
      />
    </div>
  </teleport>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  unref,
  watch,
} from 'vue'
import { pick } from 'lodash-unified'
import { useDebounceFn } from '@vueuse/core'
import { useLocale, useLockscreen, useNamespace } from '@element-plus/hooks'
import { debugWarn, isFunction, isInView, isString } from '@element-plus/utils'
import ElButton from '@element-plus/components/button'
import { ElPopover, popoverProps } from '@element-plus/components/popover'
import ElIcon from '@element-plus/components/icon'
import { Close } from '@element-plus/icons-vue'
import TourContentRender from './content-render.vue'
import ElTourV2Mask from './mask.vue'
import type { TourV2Popover, TourV2Step } from './tour'
import type { PropType, VNode } from 'vue'
import type { PopoverInstance } from '@element-plus/components/popover'

type TourMaskInstance = InstanceType<typeof ElTourV2Mask>
type Rect = {
  x?: number
  y?: number
  width?: number
  height?: number
}

export default defineComponent({
  name: 'ElTourV2',
  components: {
    ElIcon,
    Close,
    ElButton,
    ElPopover,
    ElTourV2Mask,
    TourContentRender,
  },

  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    current: {
      type: Number,
      default: 1,
    },
    steps: {
      type: Array as PropType<TourV2Step[]>,
      required: true,
    },
    mask: {
      type: [Boolean, String],
      default: true,
    },
    placement: popoverProps.placement,
    showArrow: {
      type: Boolean,
      default: true,
    },
    width: {
      type: [Number, String],
      default: 480,
    },
    type: {
      type: String,
      values: ['default', 'primary'],
      default: 'default',
    },
    zIndex: {
      type: Number,
      default: 9999,
    },
    indicatorsRender: {
      type: Function as PropType<(current: number, total: number) => VNode>,
    },
    debounce: {
      type: Number,
      default: 1000,
    },
    scrollIntoView: {
      type: Boolean,
      default: false,
    },
    scrollIntoViewOptions: {
      type: Object as PropType<ScrollIntoViewOptions>,
      default: () => ({}),
    },
    dot: {
      type: Boolean,
      default: true,
    },
    showSkip: {
      type: Boolean,
      default: true,
    },
    closeOnPressEscape: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    'update:current',
    'update:visible',
    'finish',
    'change',
    'close',
    'escape',
    'skip',
  ],

  expose: [
    'tourRef',
    'maskRef',
    'update',
    'step',
    'skip',
    'prev',
    'next',
    'localCurrent',
    'localVisible',
  ],

  setup(props, { emit }) {
    const ns = useNamespace('tour-v2')
    const { t } = useLocale()

    if (!props.steps || !props.steps.length) {
      debugWarn('ElTourV2', 'steps must not be null!')
    }

    const tourRef = ref<HTMLElement | null>(null)
    const maskRef = ref<TourMaskInstance>()
    const popover = ref<PopoverInstance>()
    const popoverReferenceRef = ref<HTMLElement | null>(null)

    const localVisible = ref(false)

    const localCurrent = ref(props.current)
    const total = ref(props.steps?.length ?? 0)
    const currentTarget = ref<HTMLElement | null>(null)
    const currentStep = ref<TourV2Step | null>(null)
    const currentTrigger = ref<Rect>({})
    const currentTriggerStyle = ref<Record<string, any>>({})
    const currentTriggerCenter = ref(false)
    const currentMask = ref<string | boolean>(props.mask)
    const currentPopProps = ref<Record<string, any>>({
      placement: props.placement,
      width: props.width,
      showArrow: props.showArrow,
    })

    const showPrev = computed(() => {
      return localCurrent.value > 1
    })

    const showNext = computed(() => {
      return localCurrent.value < total.value
    })

    const showFinish = computed(() => {
      return localCurrent.value === total.value
    })

    const tourWrapperClass = computed(() => {
      return [
        ns.b(),
        ns.m(props.type),
        currentTriggerCenter.value && ns.is('center'),
      ]
    })

    const tourStyle = computed(() => {
      return { zIndex: props.zIndex }
    })

    watch(localVisible, (val) => {
      emit('update:visible', val)
      if (val) {
        step(localCurrent.value)
        onKeyupEvent()
      } else {
        offKeyupEvent()
      }
    })

    watch(
      () => props.visible,
      (val) => {
        localVisible.value = val
      }
    )

    watch(
      () => props.current,
      (val) => {
        if (val !== localCurrent.value) {
          step(val)
        }
      }
    )

    const updatePopoverProps = (): void => {
      if (currentStep.value) {
        const step = unref(currentStep)
        // @ts-ignore
        const popProps: TourV2Popover = pick(step, [
          'placement',
          'width',
          'showArrow',
          'effect',
          'offset',
          'popperClass',
          'popperStyle',
        ])
        currentPopProps.value = {
          ...popProps,
          placement: popProps.placement ?? props.placement,
          width: popProps.width ?? props.width,
          showArrow:
            !currentTriggerCenter.value &&
            (popProps.showArrow ?? props.showArrow),
        }
      } else {
        currentPopProps.value = {
          placement: props.placement,
          width: props.width,
          showArrow: props.showArrow,
        }
      }
    }

    const normalizeTarget = (target?: any): HTMLElement | null => {
      if (!target) return null
      if (isString(target)) return document.querySelector(target) as HTMLElement
      if (isFunction(target)) return target() as HTMLElement
      return target as HTMLElement
    }

    const update = (): void => {
      if (currentTarget.value) {
        // TO 滚动置视图内
        if (props.scrollIntoView && !isInView(currentTarget.value)) {
          const scrollIntoViewOptions: ScrollIntoViewOptions = {
            behavior: 'smooth',
            block: 'center',
            ...props.scrollIntoViewOptions,
          }
          currentTarget.value.scrollIntoView(scrollIntoViewOptions)
        }
        // TO 重绘
        const rect = currentTarget.value.getBoundingClientRect()
        currentTrigger.value = {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        }
      } else {
        // TO 居中处理
        currentTrigger.value = {
          x: 0,
          y: 0,
          width: 0,
          height: 0,
        }
      }
      nextTick(() => {
        activeUpdate()
      })
    }

    const activeUpdate = (): void => {
      // mask update
      maskRef.value && maskRef.value.update()

      // trigger update
      const maskInner = unref(maskRef.value?.maskInner || {})
      if (maskInner.width && maskInner.height) {
        currentTriggerCenter.value = false
        currentTriggerStyle.value = {
          left: `${maskInner.x}px`,
          top: `${maskInner.y}px`,
          width: `${maskInner.width}px`,
          height: `${maskInner.height}px`,
        }
      } else {
        currentTriggerCenter.value = true
        currentTriggerStyle.value = {}
      }

      // content update
      updatePopoverProps()
      popover.value?.popperRef?.popperInstanceRef?.update?.()
    }

    const step = (curr: number): void => {
      if (!total.value) return
      const newCurrent = Math.max(Math.min(curr, total.value), 1)
      if (localCurrent.value !== newCurrent) {
        emit('change', newCurrent)
      }
      emit('update:current', newCurrent)
      localCurrent.value = newCurrent
      // @ts-ignore
      currentStep.value = props.steps[localCurrent.value - 1]
      if (!currentStep.value) {
        localVisible.value = false
        debugWarn('ElTourV2', 'item of steps must not be null!')
      } else {
        currentMask.value = currentStep.value?.mask ?? props.mask
        currentTarget.value = normalizeTarget(currentStep.value.target)
        update()
      }
    }

    const handleSkip = (): void => {
      localVisible.value = false
      emit('skip')
    }

    const handleEscape = (): void => {
      localVisible.value = false
      if (props.closeOnPressEscape) {
        emit('escape')
      } else {
        nextTick(() => {
          localVisible.value = true
        })
      }
    }

    const handlePrev = (): void => {
      step(localCurrent.value - 1)
      toFocus()
    }

    const handleNext = (): void => {
      step(localCurrent.value + 1)
      toFocus()
    }

    const handleFinish = (): void => {
      localVisible.value = false
      emit('finish')
    }

    const handleClose = (): void => {
      localVisible.value = false
      emit('close')
    }

    const handleKeyup = (e: KeyboardEvent): void => {
      if (!localVisible.value) return
      if (e.key === 'ArrowLeft') {
        handlePrev()
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext()
      } else if (e.key === 'Escape') {
        // TODO 按下 Escape 会隐藏popper，目前先直接隐藏tour
        // 隐藏popper 是由于 focus-trap 触发 release-requested 事件，导致 popper 触发 close 事件，将 popper 隐藏
        handleEscape()
      }
    }

    const toFocus = (): void => {
      nextTick(() => {
        tourRef.value?.focus()
      })
    }

    const onKeyupEvent = (): void => {
      setTimeout(() => {
        tourRef.value?.addEventListener('keyup', handleKeyup)
        toFocus()
      }, 1)
    }

    const offKeyupEvent = (): void => {
      tourRef.value?.removeEventListener('keyup', handleKeyup)
    }

    const resizeDebounce = useDebounceFn(() => {
      update()
    }, props.debounce)

    onMounted(() => {
      // lock screen
      useLockscreen(localVisible, { ns })
      localVisible.value = props.visible
      window.addEventListener('resize', resizeDebounce)
      if (localVisible.value) {
        step(localCurrent.value)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', resizeDebounce)
      offKeyupEvent()
    })

    return {
      // inner util
      ns,
      t,
      // ref
      tourRef,
      maskRef,
      popover,
      popoverReferenceRef,
      // data
      localVisible,
      localCurrent,
      total,
      currentTarget,
      currentStep,
      currentTrigger,
      currentTriggerStyle,
      currentTriggerCenter,
      currentMask,
      currentPopProps,
      // computed data
      showPrev,
      showNext,
      showFinish,
      tourWrapperClass,
      tourStyle,
      // method
      update,
      step,
      handleSkip,
      skip: handleSkip,
      handleEscape,
      handlePrev,
      prev: handlePrev,
      handleNext,
      next: handleNext,
      handleFinish,
      handleClose,
      toFocus,
      resizeDebounce,
    }
  },
}) as any
</script>
